import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';

// Surfaces error messages returned by the XO events microservice as toaster
// notifications. Scoped by host so non-XO traffic is unaffected. Effects keep
// their per-call catchError for state transitions; this interceptor owns the
// user-facing wording for XO failures so the GO Admin UI never has to define
// its own message for an XO error.
@Injectable()
export class XoErrorInterceptor implements HttpInterceptor {
  constructor(
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(environment.eventsBackendURL)) {
      return next.handle(req);
    }
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (
          this.shouldToast(err) &&
          !this.isOptionalFloorplanLookup(req, err)
        ) {
          this.toastr.error(this.resolveMessage(err));
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * A floor plan is optional on an event, so the editor's read-only lookups
   * answer 404 both when the event has no linked floor plan and when the venue
   * has none at all. That is an ordinary empty state, not something to tell the
   * admin about — the service maps it to a null link / empty list.
   */
  private isOptionalFloorplanLookup(
    req: HttpRequest<unknown>,
    err: HttpErrorResponse,
  ): boolean {
    return (
      err.status === 404 &&
      req.method === 'GET' &&
      (/\/v1\/venues\/\d+\/floorplans$/.test(req.url) ||
        /\/v1\/venues\/\d+\/events\/\d+\/floorplan$/.test(req.url))
    );
  }

  private shouldToast(err: HttpErrorResponse): boolean {
    // 401: existing auth-refresh flow handles it.
    if (err.status === 401) {
      return false;
    }
    // 2xx surfaced as error means the response parser tripped on an empty
    // body (e.g. backend returns 200 No Content for delete). Some effects
    // convert these back to success — don't toast over them.
    if (err.status >= 200 && err.status < 300) {
      return false;
    }
    return true;
  }

  private resolveMessage(err: HttpErrorResponse): string {
    const body = err?.error;
    if (body && typeof body === 'object') {
      const message = (body as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
      const detail = (body as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    return this.translate.instant('admin.xo.error.generic');
  }
}
